import { convertCurrencyToNumber } from 'utils/currencyConverter';
import {
  FACILITY_FIELDS,
  FACILITY_WITH_DESCRIPTION,
  TConditionalFieldName,
  TRefinancing,
  TRefinancingData,
  TRefinancingDebt,
  TRefinancingDebtData,
} from './refinancing.types';

const denormaliseDebt = (debt: TRefinancingDebt): TRefinancingDebtData => {
  const facilityType = debt.facilityType || undefined;

  const mapped: TRefinancingDebtData = {
    lender_name: debt.lenderName ?? '',
    outstanding_balance: convertCurrencyToNumber(debt.outstandingBalance) ?? 0,
    // The form only offers the seven backend facility types, so the empty
    // default should never reach here once a debt is filled in.
    facility_type: facilityType as TRefinancingDebtData['facility_type'],
  };

  // Only send the conditional fields the chosen facility type allows, so stale
  // values left in form state (react-hook-form keeps unmounted fields) never
  // leak into the payload and trip backend validation.
  const allows = (field: TConditionalFieldName) =>
    !!facilityType &&
    FACILITY_FIELDS[facilityType].some(
      (allowedField) => allowedField.name === field,
    );

  if (allows('creditLimit') && debt.creditLimit) {
    mapped.limit_amount = convertCurrencyToNumber(debt.creditLimit);
  }
  if (allows('monthlyRepayment') && debt.monthlyRepayment) {
    mapped.monthly_repayment = convertCurrencyToNumber(debt.monthlyRepayment);
  }
  if (allows('lastRepaymentDate') && debt.lastRepaymentDate) {
    mapped.last_repayment_date = debt.lastRepaymentDate;
  }
  if (facilityType === FACILITY_WITH_DESCRIPTION) {
    mapped.other_description = debt.otherDescription;
  }

  return mapped;
};

// Builds the `refinancing` block for the application payload:
// - object: debts replaced
// - null: key sent as null so the backend clears any saved debts
// The form always passes an object when the section is shown, or null from
// `clearHiddenRefinancing` when it is hidden, so there is no "leave untouched"
// (omit the key) case to represent.
export const denormaliseRefinancing = (
  refinancing?: TRefinancing | null,
): TRefinancingData | null => {
  if (!refinancing) return null;
  return { debts: refinancing.debts.map(denormaliseDebt) };
};

const normaliseDebt = (debt: TRefinancingDebtData): TRefinancingDebt => ({
  lenderName: debt.lender_name,
  facilityType: debt.facility_type,
  outstandingBalance: debt.outstanding_balance?.toString(),
  creditLimit: debt.limit_amount?.toString(),
  monthlyRepayment: debt.monthly_repayment?.toString(),
  lastRepaymentDate: debt.last_repayment_date ?? undefined,
  otherDescription: debt.other_description ?? undefined,
});

// Reads the GET `refinancing` block back into form shape so saved debts
// reappear on reload. Returns undefined when the application carries no
// refinancing data, leaving the form to seed its own empty debt.
export const normaliseRefinancing = (
  refinancing?: TRefinancingData | null,
): TRefinancing | undefined => {
  if (!refinancing) return undefined;
  return { debts: refinancing.debts.map(normaliseDebt) };
};
